import {
    escapePlaintextHtml,
    sanitizeRichHtml,
} from "./chatFrontendGuards.js?v=1.0.2"

export default {
    props: {
        message: Object
    },
    data() {
        return {
            initializationAttempts: 0,
            maxInitializationAttempts: 5,
            tablesInitialized: false
        }
    },
    computed: {
        formattedText() {
            const source = String(this.message && this.message.content || '');
            try {
                return sanitizeRichHtml(marked.parse(source), source);
            } catch (error) {
                return escapePlaintextHtml(source);
            }
        },
        messageType() {
            // Determine the type of system message based on content
            if (this.message.content.includes('[Búsqueda realizada]')) {
                return 'search-query';
            } else if (this.message.content.includes('[Resultados de búsqueda]')) {
                return 'search-results';
            } else if (this.message.content.includes('[Solicitud de jurisprudencia]')) {
                return 'jurisprudence-request';
            } else if (this.message.content.includes('[Jurisprudencia obtenida]')) {
                return 'jurisprudence-content';
            } else if (this.message.content.includes('[Solicitud de legislación]')) {
                return 'legislation-request';
            } else if (this.message.content.includes('[Legislación obtenida]')) {
                return 'legislation-content';
            } else if (this.message.content.includes('[Error')) {
                return 'error';
            }
            return 'general';
        },
        iconClass() {
            switch (this.messageType) {
                case 'search-query':
                    return 'anticon-search';
                case 'search-results':
                    return 'anticon-table';
                case 'jurisprudence-request':
                case 'jurisprudence-content':
                    return 'anticon-audit';
                case 'legislation-request':
                case 'legislation-content':
                    return 'anticon-book';
                case 'error':
                    return 'anticon-exclamation-circle';
                default:
                    return 'anticon-info-circle';
            }
        },
        messageTitle() {
            switch (this.messageType) {
                case 'search-query':
                    return 'Búsqueda Realizada';
                case 'search-results':
                    return 'Resultados de Búsqueda';
                case 'jurisprudence-request':
                    return 'Solicitud de Jurisprudencia';
                case 'jurisprudence-content':
                    return 'Jurisprudencia Obtenida';
                case 'legislation-request':
                    return 'Solicitud de Legislación';
                case 'legislation-content':
                    return 'Legislación Obtenida';
                case 'error':
                    return 'Error';
                default:
                    return 'Actividad del Sistema';
            }
        }
    },
    methods: {
        ensureTableWrapper(table) {
            if (!table || !table.parentNode) return;
            if (table.closest('.system-message-table-wrapper')) return;

            const wrapper = document.createElement('div');
            wrapper.className = 'system-message-table-wrapper';
            table.parentNode.insertBefore(wrapper, table);
            wrapper.appendChild(table);
        },
        initializeDataTables() {
            // Don't re-initialize if already done successfully
            if (this.tablesInitialized) return;

            this.$nextTick(() => {
                const initialTables = this.$el ? this.$el.querySelectorAll('table') : [];
                if (initialTables.length > 0) {
                    initialTables.forEach(table => this.ensureTableWrapper(table));
                }

                // Check if the element is visible
                const isVisible = this.$el && this.$el.offsetParent !== null;

                if (!isVisible && this.initializationAttempts < this.maxInitializationAttempts) {
                    // If not visible, retry after a delay
                    this.initializationAttempts++;
                    console.log(`[SystemMessage] Element not visible, retrying initialization (attempt ${this.initializationAttempts}/${this.maxInitializationAttempts}) for message ID: ${this.message.timestamp}`);
                    setTimeout(() => this.initializeDataTables(), 200 + (this.initializationAttempts * 100)); // Incremental backoff
                    return;
                }

                if (!isVisible && this.initializationAttempts >= this.maxInitializationAttempts) {
                    console.warn(`[SystemMessage] Max initialization attempts reached for element visibility. Tables might not initialize for message ID: ${this.message.timestamp}`);
                    // Attempt to initialize anyway, tables might become visible through other means or might be simple.
                }

                // console.log(`[SystemMessage] Element is visible or max attempts reached, proceeding with table initialization for message ID: ${this.message.timestamp}`);

                // Wait a bit longer to ensure DOM is fully rendered
                setTimeout(() => {
                    const tables = this.$el.querySelectorAll('table');
                    if (tables.length === 0) {
                        this.tablesInitialized = true; // No tables to initialize
                        console.log(`[SystemMessage] No tables found to initialize for message ID: ${this.message.timestamp}`);
                        return;
                    }

                    let allCandidateTablesSuccessfullyHandled = true;
                    let hasCandidateTables = false;

                    tables.forEach(table => {
                        this.ensureTableWrapper(table);
                        // Skip if table is already a DataTable
                        if (table.id && $.fn.DataTable && $.fn.DataTable.isDataTable('#' + table.id)) {
                            // Already initialized, consider it handled if it was a candidate
                            if (window.innerWidth <= 768 || table.querySelectorAll('th').length > 3) {
                                hasCandidateTables = true;
                            }
                            return;
                        }

                        // Add classes to tables for better responsiveness (same as BotMessage.js)
                        table.classList.add('table', 'table-bordered', 'dynamic-color');

                        // Generate a random ID for the table if it doesn't have one
                        if (!table.id) {
                            table.id = 'dt-' + Math.random().toString(36).substring(7);
                        }

                        const isCandidateForDataTable = (window.innerWidth <= 768 || table.querySelectorAll('th').length > 3);

                        if (isCandidateForDataTable) {
                            hasCandidateTables = true;
                            // Check if table is visible before initializing
                            const tableIsVisible = table.offsetParent !== null;
                            if (tableIsVisible) {
                                try {
                                    if ($.fn.DataTable && !$.fn.DataTable.isDataTable('#' + table.id)) {
                                        try {
                                            $('#' + table.id).DataTable({
                                                responsive: true,
                                                language: {
                                                    url: "/static/json/datatables_es-ES.json"
                                                },
                                                searching: false,
                                                info: false,
                                                ordering: false,
                                                paging: false,
                                                // Keep large tables contained within the chat/modal viewport
                                                scrollX: true,
                                                scrollY: '35vh',
                                                scrollCollapse: true,
                                                autoWidth: true,
                                                // Add column width constraints for better responsiveness
                                                columnDefs: [
                                                    {
                                                        targets: '_all',
                                                        className: 'dt-body-left'
                                                    }
                                                ]
                                            });
                                        } catch (error) {
                                            console.error(`[SystemMessage] Error initializing DataTable for table ${table.id} in message ID: ${this.message.timestamp}:`, error);
                                            allCandidateTablesSuccessfullyHandled = false; // Mark as not handled due to error
                                        }
                                        // console.log(`[SystemMessage] DataTable initialized for table ${table.id} in message ID: ${this.message.timestamp}`);
                                    }
                                } catch (error) {
                                    console.error(`[SystemMessage] Error initializing DataTable for table ${table.id} in message ID: ${this.message.timestamp}:`, error);
                                    allCandidateTablesSuccessfullyHandled = false; // Mark as not handled due to error
                                }
                            } else {
                                console.warn(`[SystemMessage] Candidate table ${table.id} is NOT visible within SystemMessage ${this.message.timestamp}, DataTable not initialized for it yet.`);
                                allCandidateTablesSuccessfullyHandled = false; // Not visible, so not successfully handled
                            }
                        } else {
                            // Not a candidate for DataTable (e.g., simple table on large screen).
                            // Considered "handled" for the purpose of the tablesInitialized flag.
                        }
                    });

                    if (!hasCandidateTables && tables.length > 0) {
                        this.tablesInitialized = true;
                        console.log(`[SystemMessage] No tables required DataTable initialization for message ID: ${this.message.timestamp}`);
                    } else if (hasCandidateTables && allCandidateTablesSuccessfullyHandled) {
                        this.tablesInitialized = true;
                        console.log(`[SystemMessage] All ${tables.length} candidate DataTables initialized/handled successfully for message ID: ${this.message.timestamp}`);
                    } else if (hasCandidateTables && !allCandidateTablesSuccessfullyHandled) {
                        this.tablesInitialized = false; // Explicitly set to false
                        console.warn(`[SystemMessage] Some candidate DataTables were not successfully initialized for message ID: ${this.message.timestamp}. 'tablesInitialized' remains false.`);
                    } else if (tables.length === 0) { // Should have been caught earlier, but as a fallback
                        this.tablesInitialized = true;
                    }
                }, 500); // Use same timeout as BotMessage.js
            });
        },
        checkAndReinitialize() {
            // Force re-check of table initialization
            if (!this.tablesInitialized) {
                this.initializationAttempts = 0; // Reset for a fresh attempt cycle
                console.log(`[SystemMessage] checkAndReinitialize: tables not initialized for message ID ${this.message.timestamp}, trying again.`);
                this.initializeDataTables();
            } else {
                // console.log(`[SystemMessage] checkAndReinitialize: tables already initialized for message ID ${this.message.timestamp}.`);
            }
        }
    },
    mounted() {
        this.initializeDataTables();

        // Also listen for modal shown events to reinitialize if needed
        const chatModal = document.getElementById('chatModal');
        if (chatModal) {
            chatModal.addEventListener('shown.bs.modal', () => {
                if (!this.tablesInitialized) {
                    console.log('[SystemMessage] Chat modal shown, checking table initialization');
                    this.checkAndReinitialize();
                }
            });
        }

        // Listen for custom recheck event
        this.recheckHandler = () => {
            console.log('[SystemMessage] Received recheckDataTables event');
            if (!this.tablesInitialized) {
                this.checkAndReinitialize();
            }
        };
        window.addEventListener('recheckDataTables', this.recheckHandler);
    },
    beforeDestroy() {
        // Clean up event listener
        if (this.recheckHandler) {
            window.removeEventListener('recheckDataTables', this.recheckHandler);
        }
    },
    updated() {
        // Re-initialize DataTables when component is updated
        // only if they were not successfully initialized before.
        if (!this.tablesInitialized) {
            console.log(`[SystemMessage] updated() hook triggered, tables not initialized for message ID ${this.message.timestamp}. Checking again.`);
            this.checkAndReinitialize();
        }
    },
    template: `
        <div :class="['flex', 'gap-1', 'items-start', '!pr-4', '!pt-3', '!pb-3', '!pl-2', 'bg-gray-50', 'rounded-lg', 'mb-3', 'border-l-4', 'border-blue-400', 'system-message', { 'error-message': messageType === 'error' }]">
            <div class="w-[32px] h-[32px] rounded-md overflow-hidden flex items-center justify-center bg-blue-100 shrink-0 text-blue-600">
                <i :class="['anticon', iconClass, 'text-sm']"></i>
            </div>
            <div class="flex-1 min-w-0 leading-6">
                <div class="text-sm font-medium text-blue-800 mb-1">{{ messageTitle }}</div>
                <div class="text-sm text-gray-700 system-message-content" v-html="formattedText"></div>
                <div class="text-xs text-gray-500 mt-2" v-if="message.timestamp">
                    {{ new Date(message.timestamp).toLocaleString() }}
                </div>
            </div>
        </div>
        <style>
            /* Ensure system message content does not overflow horizontally */
            .system-message .system-message-content {
                display: block;
                max-width: 100%;
                min-width: 0;
                overflow-x: auto;
                overflow-wrap: anywhere;
            }

            .system-message .system-message-table-wrapper {
                max-width: 100%;
                overflow-x: auto;
                overflow-y: hidden;
                -webkit-overflow-scrolling: touch;
            }
            
            /* Add wrapper for DataTables to control overflow better */
            .system-message .dataTables_wrapper {
                max-width: 100%;
                width: 100%;
                overflow-x: auto;
                margin-right: 0.5rem;
                box-sizing: border-box;
            }

            .system-message .dataTables_scroll,
            .system-message .dataTables_scrollHead,
            .system-message .dataTables_scrollBody,
            .system-message .dataTables_scrollHeadInner {
                max-width: 100%;
                width: 100%;
                box-sizing: border-box;
            }
            
            .system-message table {
                margin: 1rem 0;
                font-size: 0.875rem;
                border-radius: 0.375rem;
                box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
                width: 100% !important;
                table-layout: auto;
                max-width: 100%;
                box-sizing: border-box;
            }
            
            .system-message table th,
            .system-message table td {
                padding: 0.5rem !important;
                vertical-align: top;
                border: 1px solid #e5e7eb;
                max-height: 300px;
                overflow-y: auto;
                word-break: break-word;
                overflow-wrap: anywhere;
                white-space: normal !important;
            }

            .system-message table td a {
                word-break: break-all;
                overflow-wrap: anywhere;
            }
            
            .system-message table th {
                background-color: #f9fafb;
                font-weight: 600;
                color: #374151;
            }
            
            /* Ensure DataTables scrolling container respects boundaries */
            .system-message .dataTables_scrollBody {
                max-width: 100%;
                overflow-x: auto;
            }
            
            /* Add extra spacing on mobile */
            @media (max-width: 768px) {
                .system-message {
                    padding-right: 1rem !important;
                }
                .system-message .dataTables_wrapper {
                    margin-right: 0.25rem;
                }
            }
        </style>`
} 
